<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { formatTimeAgo } from '@vueuse/core'
import type { Mail, Notification } from '~/types'
import { messageBodyToPlainText } from '~/utils/sanitizeMessageHtml'

/**
 * Combined inbox + notifications surface.
 *
 * Replaces the two separate buttons in the header (bell slideover +
 * inbox icon) with a single bell button that opens a popover with two
 * tabs. Reduces header crowding (which was clipping into the household
 * selector) and gives users one consistent "do I have anything new?"
 * affordance on both desktop and mobile.
 *
 * Mobile and desktop share the same popover behavior. On narrow
 * viewports the popover is full-width via Nuxt UI's responsive defaults.
 *
 * Data sources:
 *   - Inbox preview: GET /api/mails (top 5)
 *   - Inbox unread count: from useInboxUnread composable (already
 *     polled at the layout level)
 *   - Notifications: GET /api/notifications (lazy, doesn't block paint)
 */

const inboxUnread = useInboxUnread()

const activeTab = ref<'inbox' | 'notifications'>('inbox')

// Inbox preview — limited to the 5 most recent. Full inbox stays at /inbox.
const { data: inboxPreview, refresh: refreshInbox } = useFetch<Mail[]>('/api/mails', {
  default: () => [],
  lazy: true,
  server: false,
  key: 'inbox-popover-preview',
  watch: false
})
const recentInbox = computed(() => (inboxPreview.value || []).slice(0, 5))

// Notifications — same lazy + client-only pattern as the old slideover.
// `refresh` is pulled out so we can re-fetch on popover-open and on a
// background poll (see below); useFetch does NOT self-poll.
const { data: notifications, refresh: refreshNotifications } = useFetch<Notification[]>('/api/notifications', {
  lazy: true,
  server: false,
  default: () => [] as Notification[]
})

const notificationsUnread = computed(() =>
  (notifications.value || []).filter(n => !!n.unread).length
)

// Combined badge count — shown on the bell icon outside the popover.
// Sum, not max — a user with 3 inbox + 2 notifications expects to see "5".
const totalUnread = computed(() =>
  inboxUnread.unreadCount.value + notificationsUnread.value
)

// When the user opens the popover, refresh both the inbox preview and the
// notifications list so they see anything that arrived since the last poll.
//
// We also mark notifications as seen after a 1.2s dwell on the
// Notifications tab — short enough that an intentional open marks,
// long enough that a quick "is there anything new?" peek (where the
// user reopens almost immediately to check) doesn't accidentally
// flush their unread state. The bump only fires once per popover
// session to avoid hammering the backend on every tab switch.
const { csrf } = useAuth()
const open = ref(false)
let markSeenTimer: any = null
let markSeenFired = false
async function markNotificationsSeen() {
  if (markSeenFired) return
  markSeenFired = true
  try {
    await $fetch('/api/profile/notifications-mark-seen', {
      method: 'POST',
      credentials: 'include',
      headers: { 'x-csrf': csrf.value || '' }
    })
  } catch { /* fire-and-forget; the unread state will catch up on next refresh */ }
}
function scheduleMarkSeen() {
  if (markSeenTimer) clearTimeout(markSeenTimer)
  if (markSeenFired) return
  markSeenTimer = setTimeout(markNotificationsSeen, 1200)
}
function onPopoverOpenChange(value: boolean) {
  open.value = value
  if (value) {
    refreshInbox()
    refreshNotifications()
    inboxUnread.refresh()
    // Mark seen only when the user is actually looking at the
    // Notifications tab. Inbox-tab opens shouldn't flush notification
    // state — those have their own read-tracking on individual messages.
    if (activeTab.value === 'notifications') scheduleMarkSeen()
  } else {
    if (markSeenTimer) clearTimeout(markSeenTimer)
    markSeenFired = false   // reset for next open
  }
}

// Tab switches inside the popover should also fire the dwell-timer
// when the user lands on Notifications.
watch(activeTab, (next) => {
  if (open.value && next === 'notifications') scheduleMarkSeen()
})

// Lightweight background poll so the bell badge reflects new notifications
// (e.g. a freshly-failed import or another admin's action) without a page
// reload. 90s is responsive enough while staying light on the backend; the
// inbox unread count is already polled at the layout level, so we only need
// to refresh notifications here. The timer is cleared on unmount to avoid
// leaking intervals across route changes.
const NOTIFICATIONS_POLL_MS = 90_000
let pollTimer: ReturnType<typeof setInterval> | null = null
onMounted(() => {
  pollTimer = setInterval(() => { refreshNotifications() }, NOTIFICATIONS_POLL_MS)
})
onUnmounted(() => {
  if (pollTimer) clearInterval(pollTimer)
  if (markSeenTimer) clearTimeout(markSeenTimer)
})
</script>

<template>
  <UPopover
    :open="open"
    :ui="{ content: 'w-96 max-w-[calc(100vw-2rem)]' }"
    @update:open="onPopoverOpenChange"
  >
    <UTooltip text="Inbox & notifications" :shortcuts="['N']">
      <UButton color="neutral" variant="ghost" square class="cursor-pointer">
        <UChip
          :color="totalUnread > 0 ? 'primary' : 'neutral'"
          :text="totalUnread > 0 ? String(totalUnread) : undefined"
          :show="totalUnread > 0"
          size="md"
          inset
          position="top-right"
        >
          <UIcon name="i-lucide-bell" class="size-5 shrink-0" />
        </UChip>
      </UButton>
    </UTooltip>

    <template #content>
      <div class="w-full">
        <!-- Tab strip -->
        <div class="px-3 pt-3 pb-2 border-b border-default">
          <div class="flex items-center gap-1 text-sm">
            <button
              type="button"
              class="cursor-pointer flex items-center gap-1.5 px-3 py-1.5 rounded transition-colors"
              :class="activeTab === 'inbox'
                ? 'bg-primary/10 text-primary font-semibold'
                : 'text-muted hover:bg-elevated/50'"
              @click="activeTab = 'inbox'"
            >
              <UIcon name="i-lucide-inbox" class="size-4" />
              Inbox
              <UBadge
                v-if="inboxUnread.unreadCount.value > 0"
                :label="String(inboxUnread.unreadCount.value)"
                color="primary"
                variant="subtle"
                size="sm"
              />
            </button>
            <button
              type="button"
              class="cursor-pointer flex items-center gap-1.5 px-3 py-1.5 rounded transition-colors"
              :class="activeTab === 'notifications'
                ? 'bg-primary/10 text-primary font-semibold'
                : 'text-muted hover:bg-elevated/50'"
              @click="activeTab = 'notifications'"
            >
              <UIcon name="i-lucide-bell" class="size-4" />
              Notifications
              <UBadge
                v-if="notificationsUnread > 0"
                :label="String(notificationsUnread)"
                color="primary"
                variant="subtle"
                size="sm"
              />
            </button>
          </div>
        </div>

        <!-- Tab body -->
        <div class="max-h-[60dvh] overflow-y-auto">
          <!-- Inbox -->
          <div v-show="activeTab === 'inbox'">
            <div v-if="!recentInbox.length" class="px-4 py-8 text-center text-sm text-muted">
              <!-- `block` is required for `mx-auto` to actually center —
                   UIcon renders as inline-block, and auto margins only
                   center block elements. -->
              <UIcon name="i-lucide-inbox" class="block size-8 text-dimmed mx-auto mb-2" />
              No messages.
            </div>
            <NuxtLink
              v-for="m in recentInbox"
              :key="m.id"
              :to="`/inbox?id=${m.id}`"
              class="cursor-pointer block px-4 py-3 hover:bg-elevated/50 border-b border-gray-100 last:border-b-0"
              @click="open = false"
            >
              <div class="flex items-start justify-between gap-2 mb-1">
                <p
                  class="text-sm truncate"
                  :class="m.unread ? 'font-semibold text-highlighted' : 'text-toned'"
                >{{ m.from.name }}</p>
                <time class="text-xs text-muted shrink-0">{{ formatTimeAgo(new Date(m.date)) }}</time>
              </div>
              <p
                class="text-sm truncate"
                :class="m.unread ? 'font-medium' : ''"
              >{{ m.subject }}</p>
              <p class="text-xs text-dimmed truncate">{{ messageBodyToPlainText(m.body, m.bodyFormat) }}</p>
            </NuxtLink>
          </div>

          <!-- Notifications -->
          <div v-show="activeTab === 'notifications'">
            <div v-if="!notifications?.length" class="px-4 py-8 text-center text-sm text-muted">
              <UIcon name="i-lucide-bell-off" class="block size-8 text-dimmed mx-auto mb-2" />
              No notifications.
            </div>
            <!-- Notification rows link to /notifications#{id} so the
                 full /notifications page opens with the relevant row
                 pre-expanded and scrolled into view. Earlier these
                 linked to /inbox?id= which was wrong for sign-in and
                 audit-event notifications — those have nothing in
                 the inbox to land on. -->
            <NuxtLink
              v-for="n in notifications"
              :key="n.id"
              :to="`/notifications#${encodeURIComponent(String(n.id))}`"
              :title="n.body"
              class="cursor-pointer flex items-center gap-3 px-4 py-3 hover:bg-elevated/50 border-b border-gray-100 last:border-b-0"
              @click="open = false"
            >
              <UChip color="error" :show="!!n.unread" inset>
                <UAvatar
                  v-bind="(n.sender as any).avatar"
                  :alt="n.sender.name"
                  size="md"
                />
              </UChip>
              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between gap-2">
                  <!-- Title is the row heading (action summary).
                       Falls back to sender.name for any legacy
                       notification that doesn't carry a title yet. -->
                  <span class="text-sm font-medium text-highlighted truncate">{{ (n as any).title || n.sender.name }}</span>
                  <time class="text-xs text-muted shrink-0">{{ formatTimeAgo(new Date(n.date)) }}</time>
                </div>
                <p class="text-xs text-dimmed truncate">{{ n.body }}</p>
              </div>
            </NuxtLink>
          </div>
        </div>

        <!-- Footer — unread count reflects the CURRENTLY VISIBLE tab,
             not the combined total. Previously the inbox tab footer
             would show "6 unread" when there were 0 inbox messages and
             6 notifications — that mismatch read as a bug. The
             "View all →" link also tracks the active tab so the right
             destination opens. -->
        <div class="px-3 py-2 border-t border-default flex items-center justify-between gap-2 bg-gray-50/50">
          <span class="text-xs text-muted">
            <template v-if="activeTab === 'inbox' && inboxUnread.unreadCount.value > 0">
              {{ inboxUnread.unreadCount.value }} unread
            </template>
            <template v-else-if="activeTab === 'notifications' && notificationsUnread > 0">
              {{ notificationsUnread }} unread
            </template>
            <template v-else>All caught up</template>
          </span>
          <NuxtLink
            v-if="activeTab === 'inbox'"
            to="/inbox"
            class="cursor-pointer text-xs text-primary hover:underline"
            @click="open = false"
          >View full inbox →</NuxtLink>
          <NuxtLink
            v-else
            to="/notifications"
            class="cursor-pointer text-xs text-primary hover:underline"
            @click="open = false"
          >View all notifications →</NuxtLink>
        </div>
      </div>
    </template>
  </UPopover>
</template>
